import React, { useState, useEffect } from "react";
import {
  AlarmCheck,
  GitBranch,
  ShieldCheck,
  Workflow,
  Users,
  ClipboardList,
  Bot,
  FileText,
  LayoutDashboard,
  Zap,
  FileLock,
  Lightbulb,
} from "lucide-react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

const highLevelFeatures = [
  {
    icon: <Workflow className="w-5 h-5 text-[#5652CF]" />,
    title: "Advanced Workflow Automation",
    description:
      "Design and execute complex, multi-step workflows across teams and systems.",
  },
  {
    icon: <LayoutDashboard className="w-5 h-5 text-[#5652CF]" />,
    title: "Intelligent Project Tracking",
    description:
      "Autonomous agent to track end-to-end project lifecycle to identify risks and delays early.",
  },
  {
    icon: <Zap className="w-5 h-5 text-[#5652CF]" />,
    title: "Improved Accuracy & Latency",
    description:
      "Perform tasks over enterprise apps with 2x speed and up to 50% more accuracy.",
  },
  {
    icon: <FileLock className="w-5 h-5 text-[#5652CF]" />,
    title: "Enterprise-Grade Compliance",
    description:
      "Built-in support for SOC 2, GDPR, and role-based access controls.",
  },
  {
    icon: <ShieldCheck className="w-5 h-5 text-[#5652CF]" />,
    title: "Custom SLAs & Uptime Guarantees",
    description:
      "99.9% uptime, dedicated success team, and tailored onboarding.",
  },
  {
    icon: <Users className="w-5 h-5 text-[#5652CF]" />,
    title: "Scalable Integrations",
    description:
      "Connect to Jira, Slack, GitHub, Salesforce, and internal APIs seamlessly.",
  },
];

const detailedFeatures = [
  {
    icon: <AlarmCheck className="w-5 h-5 text-[#5652CF]" />,
    title: "Preventing Delays & Human Errors",
    feature: "AI Sprint Tracking, GitHub Monitoring & Smart Escalations",
    points: [
      <>
        <GitBranch className="inline w-4 h-4 text-yellow-400 mr-2" />
        Tracks real-time task progress across Jira and GitHub.
      </>,
      <>
        <Zap className="inline w-4 h-4 text-pink-500 mr-2" />
        Detects blockers, idle tasks, and potential delays automatically.
      </>,
      <>
        <ShieldCheck className="inline w-4 h-4 text-green-500 mr-2" />
        Reviews PRs for code quality, vulnerabilities, and acceptance criteria.
      </>,
      <>
        <Users className="inline w-4 h-4 text-indigo-400 mr-2" />
        Notifies stakeholders and escalates only when necessary.
      </>,
    ],
    impact:
      "Catches blockers early, ensures quality delivery, and keeps teams aligned without micromanagement.",
  },
  {
    icon: <ClipboardList className="w-5 h-5 text-[#5652CF]" />,
    title: "Capturing & Centralizing Decisions",
    feature: "Centralized Decision Tracking & RACI-Based Notifications",
    points: [
      <>
        <FileText className="inline w-4 h-4 text-cyan-400 mr-2" />
        All project updates and decisions are centrally recorded.
      </>,
      <>
        <Users className="inline w-4 h-4 text-fuchsia-400 mr-2" />
        Stakeholders auto-notified based on RACI roles.
      </>,
      <>
        <LayoutDashboard className="inline w-4 h-4 text-orange-400 mr-2" />
        Tracks decisions across Jira, Slack, Notion, etc.
      </>,
    ],
    impact:
      "Improves visibility, ensures stakeholder alignment, and prevents rework due to missed decisions.",
  },
  {
    icon: <Workflow className="w-5 h-5 text-[#5652CF]" />,
    title: "Automating Complex Repetitive Workflows",
    feature: "Custom Workflow Builder for Product Ops",
    points: [
      <>
        <Workflow className="inline w-4 h-4 text-purple-400 mr-2" />
        PMs can configure workflows like PRD-to-Jira conversion, story
        breakdown, etc.
      </>,
    ],
    impact:
      "Reduces manual effort and streamlines repetitive product management tasks.",
  },
  {
    icon: <Bot className="w-5 h-5 text-[#5652CF]" />,
    title: "Solving Manual & Repetitive Workload",
    feature: "AI-Driven Follow-Ups, Reporting & Task Reviews",
    points: [
      <>
        <Bot className="inline w-4 h-4 text-blue-400 mr-2" />
        Automates daily follow-ups with engineers.
      </>,
      <>
        <FileText className="inline w-4 h-4 text-green-400 mr-2" />
        Sends project status updates to stakeholders.
      </>,
      <>
        <ClipboardList className="inline w-4 h-4 text-yellow-400 mr-2" />
        Performs initial task reviews.
      </>,
    ],
    impact:
      "Eliminates repetitive PM work and drastically reduces micromanagement.",
  },
];

const PremiumFeatures = ({
  setShowPremium
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const location = useLocation();
  const isOnChatScreen = location.pathname === "/chat";

  return (
    <div className="bg-black text-zinc-200 px-4 py-6 flex  items-center justify-center rounded-lg shadow-lg">
      <div className="max-w-6xl w-full">
        {!showDetails ? (
          <>
            {/* Enterprise Plan Notice */}
            <div className="bg-zinc-800 p-6 rounded-xl mb-10 text-center border border-zinc-700">
              <h2 className="text-2xl font-bold text-white mb-2">
                This feature is part of our Enterprise Plan
              </h2>
              <p className="text-zinc-400 mb-4">
                To unlock premium capabilities built for scale, request access
                to upgrade.
              </p>
              <div className="relative mt-12">
                {/* Request Access Button */}
                <div className="text-center">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-2xl transition shadow">
                    Request Enterprise Access
                  </button>
                </div>

                {/* Preview Feature Button - Bottom Right Floating */}
                <div className="absolute right-0 -mb-3 bottom-0 -mr-2">
                  <button
                    onClick={() => {
                      if(!isOnChatScreen) {
                        toast("Launching preview...");
                        setTimeout(() => {
                          setShowPremium(true);
                        }, 2000); // 2 seconds delay
                      }else {
                      toast("Redirecting to Home...");
                      // Redirect to the home/chat page
                      window.location.href = "/chat";
                      }
                    }}
                    className="group flex items-center gap-2 text-sm text-blue-400 hover:text-white bg-[#5652CF]/10 border border-[#5652CF] hover:border-blue-400 rounded-full px-3 py-1 transition-all duration-200 shadow-sm"
                  >
                    <LayoutDashboard className="w-4 h-4 text-green-400 group-hover:rotate-6 transition-transform duration-200" />
                    <span className="font-medium">{isOnChatScreen ? "Home" : "Preview Feature"}</span>
                    <svg
                      className="w-4 h-4 transition-transform group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Features */}
            <h3 className="text-xl font-semibold text-white text-center mb-6">
              What You Get with the Enterprise Plan
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {highLevelFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-zinc-900 p-4 rounded-xl border border-zinc-800 shadow-sm flex items-start gap-4"
                >
                  <div className="mt-1">{feature.icon}</div>
                  <div>
                    <h4 className="font-semibold text-zinc-100">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-zinc-400">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Chip-style CTA */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowDetails(true)}
                className="group flex items-center gap-2 text-sm text-blue-400 hover:text-white bg-[#5652CF]/10 border border-[#5652CF] hover:border-blue-400 rounded-full px-3 py-1 transition-all duration-200 shadow-sm"
              >
                <Lightbulb className="w-4 h-4 text-yellow-400 group-hover:rotate-6 transition-transform duration-200" />
                <span className="font-medium">See how it helps</span>
                <svg
                  className="w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Detailed Feature Impact */}
            <h3 className="text-2xl font-bold text-white text-center mb-10">
              How It Helps Your Team
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {detailedFeatures.map((item, index) => (
                <div
                  key={index}
                  className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6 shadow-md"
                >
                  <div className="flex items-start gap-2 mb-2">
                    <div className="mt-1">{item.icon}</div>
                    <div>
                      <h2 className="text-xl font-semibold text-zinc-100">
                        {item.title}
                      </h2>
                      <p className="text-sm text-blue-400 font-medium">
                        {item.feature}
                      </p>
                    </div>
                  </div>
                  <div className="list-disc list-inside text-zinc-300 text-sm mb-3 space-y-1">
                    {item.points.map((point, i) => (
                      <div key={i}>{point}</div>
                    ))}
                  </div>
                  <p className="text-sm text-green-400">✅ {item.impact}</p>
                </div>
              ))}
            </div>

            {/* Footer Actions */}
            <div className="text-center mt-12 flex flex-col items-center gap-4">
              <button
                onClick={() => setShowDetails(false)}
                className="text-sm text-blue-400 hover:underline"
              >
                ← Back to Overview
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-2xl transition shadow">
                Request Enterprise Access
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PremiumFeatures;